import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  FileText, Clock, BookOpen, Calculator, FlaskConical,
  ChevronRight, Award, CheckCircle2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const ExamPracticePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, api } = useAuth();
  const [satsPapers, setSatsPapers] = useState([]);
  const [gcsePapers, setGcsePapers] = useState([]);
  const [examBoards, setExamBoards] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState('all');
  const [loading, setLoading] = useState(true);

  // Detect if in admin preview mode
  const isAdminPreview = location.pathname.startsWith('/admin/preview');
  const basePath = isAdminPreview ? '/admin/preview/exams' : '/student/exams';

  useEffect(() => {
    const fetchExamData = async () => {
      try {
        const [satsRes, gcseRes] = await Promise.all([
          api.get('/v2/exams/sats/papers'),
          api.get('/v2/exams/gcse/papers')
        ]);
        setSatsPapers(satsRes.data.papers || []);
        setGcsePapers(gcseRes.data.papers || []);
        
        // Extract unique boards from GCSE papers
        const boards = [...new Set((gcseRes.data.papers || []).map(p => p.board))];
        setExamBoards(boards.map(b => ({ id: b, name: b.toUpperCase() })));
      } catch (error) {
        console.error('Error fetching exam data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchExamData();
  }, [api]);

  const subjectIcons = {
    maths: { icon: Calculator, color: 'amber' },
    english: { icon: BookOpen, color: 'pink' },
    science: { icon: FlaskConical, color: 'emerald' },
  };

  const filteredGcse = selectedBoard === 'all' 
    ? gcsePapers 
    : gcsePapers.filter(p => p.board === selectedBoard);

  // Show all papers for testing - in production, filter by key_stage
  const showSats = true; // Was: ['ks2', '11plus'].includes(user?.key_stage);
  const showGcse = true; // Was: ['ks4'].includes(user?.key_stage);

  const PaperCard = ({ paper }) => {
    const subjInfo = subjectIcons[paper.subject] || { icon: FileText, color: 'gray' };
    
    return (
      <div className="student-card" data-testid={`paper-${paper.id}`}>
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 bg-${subjInfo.color}-400 rounded-xl border-2 border-gray-800 flex items-center justify-center`}>
            <subjInfo.icon className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-bold text-slate-800">{paper.name}</h3>
            <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {paper.duration} mins
              </span>
              <span className="flex items-center gap-1">
                <FileText className="w-4 h-4" />
                {paper.questions} questions
              </span>
            </div>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize bg-${subjInfo.color}-100 text-${subjInfo.color}-700`}>
            {paper.subject}
          </span>
          <Button 
            className="student-btn py-2 px-4 text-sm"
            onClick={() => navigate(`${basePath}/${paper.id}`)}
            data-testid={`start-${paper.id}`}
          >
            Start Practice
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <StudentLayout>
      <div data-testid="exam-practice-page">
        {/* Header */}
        <div className="student-card bg-emerald-100 border-emerald-300 mb-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-emerald-500 rounded-2xl border-2 border-gray-800 flex items-center justify-center animate-float">
                <FileText className="w-10 h-10 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-3xl font-bold text-slate-800">Exam Practice</h1>
                <p className="text-slate-600">Prepare for your exams with real practice papers</p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl border-2 border-emerald-300">
              <Award className="w-5 h-5 text-emerald-500" />
              <span className="text-slate-600">Practice makes perfect!</span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-emerald-600 border-t-transparent"></div>
          </div>
        ) : (
          <Tabs defaultValue={showSats ? "sats" : "gcse"} className="space-y-6">
            <TabsList className="bg-white border-2 border-gray-800 p-1 rounded-xl">
              {showSats && (
                <TabsTrigger 
                  value="sats" 
                  className="rounded-lg font-bold data-[state=active]:bg-purple-500 data-[state=active]:text-white"
                  data-testid="sats-tab"
                >
                  Year 6 SATs
                </TabsTrigger>
              )}
              {showGcse && (
                <TabsTrigger 
                  value="gcse" 
                  className="rounded-lg font-bold data-[state=active]:bg-purple-500 data-[state=active]:text-white"
                  data-testid="gcse-tab"
                >
                  GCSE Papers
                </TabsTrigger>
              )}
              {!showSats && !showGcse && (
                <>
                  <TabsTrigger 
                    value="sats" 
                    className="rounded-lg font-bold data-[state=active]:bg-purple-500 data-[state=active]:text-white"
                  >
                    Year 6 SATs
                  </TabsTrigger>
                  <TabsTrigger 
                    value="gcse" 
                    className="rounded-lg font-bold data-[state=active]:bg-purple-500 data-[state=active]:text-white"
                  >
                    GCSE Papers
                  </TabsTrigger>
                </>
              )}
            </TabsList>

            {/* SATs Section */}
            <TabsContent value="sats">
              <div className="mb-6">
                <h2 className="font-heading text-xl font-bold text-slate-800 mb-2">Year 6 SATs Practice</h2>
                <p className="text-slate-600">Practice papers aligned with the National Curriculum tests</p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {satsPapers.map(paper => (
                  <PaperCard key={paper.id} paper={paper} />
                ))}
              </div>
            </TabsContent>

            {/* GCSE Section */}
            <TabsContent value="gcse">
              <div className="mb-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h2 className="font-heading text-xl font-bold text-slate-800 mb-2">GCSE Practice Papers</h2>
                    <p className="text-slate-600">Papers from all major exam boards</p>
                  </div>
                  
                  {/* Board Filter */}
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setSelectedBoard('all')}
                      className={`px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${
                        selectedBoard === 'all' 
                          ? 'bg-purple-500 text-white' 
                          : 'bg-white border-2 border-gray-300 text-slate-600 hover:border-purple-300'
                      }`}
                      data-testid="filter-all"
                    >
                      All Boards
                    </button>
                    {examBoards.map(board => (
                      <button
                        key={board.id}
                        onClick={() => setSelectedBoard(board.id)}
                        className={`px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${
                          selectedBoard === board.id 
                            ? 'bg-purple-500 text-white' 
                            : 'bg-white border-2 border-gray-300 text-slate-600 hover:border-purple-300'
                        }`}
                        data-testid={`filter-${board.id}`}
                      >
                        {board.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {filteredGcse.map(paper => (
                  <PaperCard key={paper.id} paper={paper} />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        )}

        {/* Tips */}
        <div className="mt-8 student-card bg-blue-50 border-blue-300">
          <h3 className="font-heading text-lg font-bold text-slate-800 mb-3">Exam Tips</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-blue-500 mt-0.5" />
              <div>
                <p className="font-semibold text-slate-700">Time yourself</p>
                <p className="text-sm text-slate-500">Practice under exam conditions</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-blue-500 mt-0.5" />
              <div>
                <p className="font-semibold text-slate-700">Read carefully</p>
                <p className="text-sm text-slate-500">Understand questions before answering</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-blue-500 mt-0.5" />
              <div>
                <p className="font-semibold text-slate-700">Show your working</p>
                <p className="text-sm text-slate-500">You can get marks for method</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-blue-500 mt-0.5" />
              <div>
                <p className="font-semibold text-slate-700">Review mistakes</p>
                <p className="text-sm text-slate-500">Learn from what you got wrong</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </StudentLayout>
  );
};

export default ExamPracticePage;
